<template>
  <div data-view="labels">
    <router-view />

    <dialog-confirm
      :text="
        $t('are_you_sure_you_want_to_delete_this_label', {
          labelName: labelForDeletion.name,
        })
      "
      :is-on="isRemoveLabelModalOn"
      :is-loading="isRemovingLabel"
      :on-cancel="toggleDeleteLabelModal"
      :on-confirm="deleteLabel"
    />

    <layout-logged-frame :title="$t('labels') + ` (${totalCount})`">
      <template v-slot:header-actions>
        <v-btn
          v-if="canStoreLabel"
          color="primary"
          @click="$router.push({ name: 'labels-new' })"
        >
          <v-icon>add</v-icon>
          {{ $t('add_new_label') }}
        </v-btn>
      </template>

      <template v-slot:content>
        <LabelsFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="labels"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectLabel"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-action="onAction"
        >
          <template v-slot:item-cell="scope">
            <div
              v-if="scope.cell.column.value === 'actions' && canDeleteLabel"
              class="remove-label-box"
              @click="
                (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onAction(scope.cell.item, scope.cell.column.action);
                }
              "
            >
              <v-icon>{{ scope.cell.column.icon }}</v-icon>
              {{ $t('delete') }}
            </div>

            <template v-if="scope.cell.column.value === 'color'">
              <labels-color
                :color="scope.cell.item.color.name.toLowerCase()"
                :label="scope.cell.item.color.name"
              />
            </template>

            <template v-if="scope.cell.column.value === 'type'">
              {{ displayColumnType(scope.cell.item) }}
            </template>

            <template
              v-else-if="
                scope.cell.column.value !== 'actions' &&
                  scope.cell.column.value !== 'color'
              "
            >
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import LabelsFilters from '../../components/forms/filters/Label.vue';
  import {extractNestedProp, isStr} from '@/utils';
  import columns from '../../config/labels/columns';
  import type { LabelListItem } from '@/types';
  import type { LabelsQueryParams } from '@/api/merchy/labels';
  import type { TableColumn } from '@/components/table/types';

  export default defineComponent({
    components: {
      LabelsFilters,
    },
    data() {
      return {
        columns: columns as TableColumn[],
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: '',
        },
        labelForDeletion: {} as Partial<LabelListItem>,
        totalPages: 0,
        query: {} as LabelsQueryParams,
        isLoading: false,
        isRemoveLabelModalOn: false,
        isRemovingLabel: false,
      };
    },
    computed: {
      labels() {
        return this.$store.getters['labels/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },

      canDeleteLabel() {
        return this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['delete'] }]);
      },
      canStoreLabel() {
        return this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['store'] }]);
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to: any, from: any) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'labels-new' || from.name === 'labels-edit') &&
          to.name === 'labels'
        ) {
          this.getLabels(this.query);
        }
      },
    },
    created() {
      if (!this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['delete'] }])) {
        this.columns = this.columns.filter(
          (column) => column.value !== 'actions',
        );
      }
    },
    methods: {
      getLabels({
        page = 1,
        order = 'asc',
        sort = '',
        search = '',
        ...rest
      }: LabelsQueryParams = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          ...rest
        };

        this.isLoading = true;
        this.pagination.order = order;

        return this.$store
          .dispatch('labels/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['labels/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectLabel(label: LabelListItem) {
        this.$store.dispatch('labels/setActiveItem', label);
        this.$router.push({ name: 'labels-edit', params: { id: label.id } });
      },
      onUpdatePagination({ page, order, sortBy }: { page: number, order: string, sortBy?: string }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.getLabels(nextQuery);
      },
      onChangePage(page: number) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getLabels(nextQuery);
      },
      onFilter(filters: { search: string, countries?: string[] }) {
        this.query = {
          ...this.query,
          ...this.getSearchQuery(filters),
        };

        this.getLabels(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = {sort: sortBy};

        this.getLabels(this.query);
      },
      getSearchQuery({ search, ...rest }: { search: string } & LabelsQueryParams): LabelsQueryParams {
        const query: LabelsQueryParams = {...rest};

        query.search = search;

        return query;
      },
      toggleDeleteLabelModal() {
        this.isRemoveLabelModalOn = !this.isRemoveLabelModalOn;
      },
      onAction(label: LabelListItem, action: string) {
        if (action === 'delete') {
          this.labelForDeletion = label;
          this.isRemoveLabelModalOn = true;
        }
      },
      deleteLabel() {
        this.isRemovingLabel = true;

        return this.$store
          .dispatch('labels/remove', this.labelForDeletion)
          .then((res) => {
            this.labelForDeletion = {};
            this.isRemovingLabel = false;
            this.toggleDeleteLabelModal();
            this.getLabels(this.query);
            return res;
          })
          .catch((err) => {
            this.isRemovingLabel = false;
            return err;
          });
      },
      getColumnValue(value: string) {
        return value.indexOf('.') === -1 ? value : value.split('.').join('-');
      },
      displayColumnValue(item: LabelListItem, columnValue: string) {
        return extractNestedProp(item, columnValue);
      },
      displayColumnType(item: LabelListItem) {
        return item.is_system_label ? 'System' : 'General';
      }
    },
  });
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

[data-view='labels'] {
  .remove-label-box {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: map.get($colors, error);

    .v-icon {
      color: map.get($colors, error);
      margin: 0 4px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
